import styled from '@emotion/styled';
import {OrbitControls} from '@react-three/drei';
import {Canvas} from '@react-three/fiber';
import {XR} from '@react-three/xr';
import {useEffect, useRef, useState} from 'react';
import {Camera, Vector3} from 'three';
import {VR_POSITIONS} from '../../constants/vrPositions';
import {AccommodationExercise} from '../../scenes/exercises/AccommodationExercise';
import {ConvergenceExercise} from '../../scenes/exercises/ConvergenceExercise';
import {Figure8Exercise} from '../../scenes/exercises/Figure8Exercise';
import {PursuitExercise} from '../../scenes/exercises/PursuitExercise';
import {SaccadesExercise} from '../../scenes/exercises/SaccadesExercise';
import {xrStore} from '../../store/xrStore';
import {ExerciseSettings, TrainingMode} from '../../types/exercises';
import {Layer} from '../../types/layers';
import {logger} from '../../utils/logger';

interface ExercisePreviewProps {
  exercise: TrainingMode;
  eye?: 'left' | 'right';
  width?: string;
  height?: string;
  showExercise?: boolean;
  containerClassName?: string;
  settings?: ExerciseSettings;
}

const PreviewContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  background-color: var(--foreground);
  border-radius: 0;
  display: flex;

  canvas {
    width: 100% !important;
    height: 100% !important;
    object-fit: contain;
  }
`;

export const ExercisePreview: React.FC<ExercisePreviewProps> = ({
  exercise,
  eye = 'left',
  width = '100%',
  height = '100%',
  showExercise = true,
  containerClassName = '',
  settings,
}) => {
  const cameraRef = useRef<Camera>();
  const [canvasSize, setCanvasSize] = useState({width: 0, height: 0});
  const containerRef = useRef<HTMLDivElement>(null);

  // Observe container size changes
  useEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      if (containerRef.current) {
        const {width, height} = containerRef.current.getBoundingClientRect();
        setCanvasSize({width, height});
      }
    };

    // Initial size
    updateSize();

    // Create observer for size changes
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(containerRef.current);

    // Listen to window resize events as well
    window.addEventListener('resize', updateSize);

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  useEffect(() => {
    if (cameraRef.current) {
      // Enable both DEFAULT and eye-specific layers
      cameraRef.current.layers.enableAll();
      if (eye === 'left') {
        cameraRef.current.layers.disable(Layer.RIGHT);
      } else {
        cameraRef.current.layers.disable(Layer.LEFT);
      }
    }
  }, [eye]);

  // Get camera settings based on exercise type
  const getCameraSettings = () => {
    switch (exercise) {
      case 'saccades':
      case 'pursuit':
      case 'convergence':
      case 'accommodation':
      case 'figure8':
      default:
        return {
          position: new Vector3(0, VR_POSITIONS.CAMERA.HEIGHT, 2),
          fov: 60,
          target: new Vector3(0, VR_POSITIONS.EXERCISE.TARGET_HEIGHT, -2),
        };
    }
  };

  const {position, fov, target} = getCameraSettings();

  const renderExercise = () => {
    const exerciseProps = settings
      ? {
          eyeToTrain: settings.eyeToTrain,
          isRedGreen: settings.isRedGreen,
          isMFBF: settings.isMFBF,
          isOverlay: settings.isOverlay,
          leftEyeColor: settings.leftEyeColor,
          rightEyeColor: settings.rightEyeColor,
          leftEyeOverlay: settings.leftEyeOverlay,
          rightEyeOverlay: settings.rightEyeOverlay,
          speed: settings.speed,
          range: settings.range,
          distance: settings.distance,
          ballPosition: settings.ballPosition,
        }
      : {};

    switch (exercise) {
      case 'saccades':
        return <SaccadesExercise {...exerciseProps} />;
      case 'pursuit':
        return <PursuitExercise {...exerciseProps} />;
      case 'convergence':
        return <ConvergenceExercise {...exerciseProps} />;
      case 'accommodation':
        return <AccommodationExercise {...exerciseProps} />;
      case 'figure8':
        return <Figure8Exercise {...exerciseProps} />;
      default:
        return null;
    }
  };

  // Adjust camera FOV based on container aspect ratio
  const aspectRatio = canvasSize.width / Math.max(1, canvasSize.height);
  const adjustedFOV = Math.min(fov * (aspectRatio < 1 ? 1.2 : 1), 75);

  return (
    <PreviewContainer ref={containerRef} style={{width, height}} className={containerClassName}>
      <Canvas
        camera={{
          position: [position.x, position.y, position.z],
          fov: adjustedFOV,
          near: 0.01,
          far: 1000,
        }}
        gl={{alpha: false, antialias: true}}
        style={{background: '#000000'}}
        frameloop="always"
        onCreated={({camera, gl}) => {
          cameraRef.current = camera;
          // Enable both DEFAULT and eye-specific layers
          camera.layers.enableAll();
          if (eye === 'left') {
            camera.layers.disable(Layer.RIGHT);
          } else {
            camera.layers.disable(Layer.LEFT);
          }

          // Add context loss handling
          gl.domElement.addEventListener('webglcontextlost', event => {
            event.preventDefault();
            logger.error('WebGL context lost in ExercisePreview');
          });

          gl.domElement.addEventListener('webglcontextrestored', () => {
            logger.info('WebGL context restored in ExercisePreview');
            camera.layers.enableAll();
            if (eye === 'left') {
              camera.layers.disable(Layer.RIGHT);
            } else {
              camera.layers.disable(Layer.LEFT);
            }
          });
        }}
      >
        <XR store={xrStore}>
          <ambientLight intensity={0.1} />
          <directionalLight position={[10, 10, 5]} intensity={0.5} />
          {showExercise && renderExercise()}
          <OrbitControls
            enablePan={true}
            enableZoom={false}
            enableRotate={true}
            minDistance={1}
            maxDistance={10}
            minPolarAngle={0}
            maxPolarAngle={Math.PI}
            target={[target.x, target.y, target.z]}
            mouseButtons={{
              LEFT: undefined,
              MIDDLE: undefined,
              RIGHT: undefined,
            }}
          />
        </XR>
      </Canvas>
    </PreviewContainer>
  );
};
